import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Pages from '../../../Data/Editor.json';
import { useEffect, useState } from 'react';
import InputField from '../../../Components/InputField/InputField';
import Checkbox from '../../../Components/Checkbox/Checkbox';
import Dropdown from '../../../Components/Dropdown/Dropdown';
import TextArea from '../../../Components/TextArea/TextArea';
import Button from '../../../Components/Button/Button';
import Scopes from '../../../Data/Scope.json';
import Loader from '../../../Components/Loader/Loader';
import styles from './editor.module.scss';
import DatePicker from '../../../Components/DatePicker/DatePicker';
import { Helmet } from 'react-helmet-async';
import { createContractServices, createItem, deleteItem, downloadInvoiceFile, getItemById, receivePayment, requestStatementCsv, updateItem } from '../../../Services/api.service';
import { useRecoilState } from 'recoil';
import { previousPage } from '../../../Recoil/Atoms';
import Cell from '../../../Components/Cell/Cell';
import TextEditor from '../../../Components/TextEditor/TextEditor';
import MultiSelectBox from '../../../Components/MultiSelectBox/MultiSelectBox';

const Editor = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const role = localStorage.getItem('role');
    const { title, id } = useParams();
    const [page, setPage] = useState({});
    const [currentItem, setCurrentItem] = useState({});
    const [loading, setLoading] = useState(true);
    const [, setPrevious] = useRecoilState(previousPage);
    const [editMode, setEditMode] = useState(true);

    useEffect(() => {
        setPrevious(location.pathname);
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const foundPage = Pages.find((p) => p.title === title);
            setPage(foundPage || {});

            if (id !== "new") {
                const getItemResponse = await getItemById(title, id) || {};
                setCurrentItem(getItemResponse);
            } else {
                setEditMode(false);
                setCurrentItem(
                    Object.fromEntries(
                        Object.keys(foundPage.inputFields)
                            .filter((key) => !foundPage.ignoreKeysForNewObject.includes(key)) // Exclude ignored keys
                            .map((key) => [key, '']) // Map remaining keys to empty string values
                    )
                );
            }
            setLoading(false);
        })();
    // eslint-disable-next-line
    }, [title, id]);

    const inputRenderHelper = (key, value) => {
        if (page['inputFields'][key]) {
            const inputObj = page['inputFields'][key];
            const requiredKeys = page['requiredKeys'];
            const label = inputObj['label'];
            const type = inputObj['type'];
            const disabled = inputObj['disabled'];
            const dropdownCall = inputObj['objectType'];
            const displayKey = inputObj['displayKey'];
            let required = requiredKeys.includes(key);

            switch (type) {
                case 'input':
                    return (
                        <InputField
                            key={key}
                            label={label}
                            required={required}
                            disabled={disabled || role !== 'Admin'}
                            value={value ? value : ''}
                            onChange={(e) => {
                                const newValue = e.target.value;
            
                                if (key === 'subTotal') {
                                    const subTotal = newValue || 0;
                                    const tax = +(subTotal * 0.13).toFixed(2);
                                    const total = +(subTotal * 1.13).toFixed(2);
            
                                    setCurrentItem((prev) => ({
                                        ...prev,
                                        subTotal,
                                        tax,
                                        total,
                                    }));
                                } else {
                                    setCurrentItem((prev) => ({
                                        ...prev,
                                        [key]: newValue,
                                    }));
                                }
                            }}
                        />
                    );
                case 'dropdown':
                    return (
                        <Dropdown
                            key={key}
                            label={label}
                            required={required}
                            value={value ? value : -1}
                            objectType={dropdownCall}
                            displayKey={displayKey}
                            onChange={async (e) => {
                                setCurrentItem((prev) => ({
                                    ...prev,
                                    [key]: e.target.value,
                                }));
                            }}
                            disabled={label !== 'Employee' && role !== 'Admin'}
                        />
                    );
                case 'textarea':
                    if (key === "companyNameList" || key === "trades") {
                        const formattedValue = Array.isArray(value) ? value.join('\n') : value;
                        return (
                            <TextArea
                                key={key}
                                label={label}
                                required={required}
                                disabled={disabled}
                                value={formattedValue || ''}
                                onChange={(e) => {
                                    setCurrentItem((prev) => ({
                                        ...prev,
                                        [key]: e.target.value,
                                    }));
                                }}
                            />
                        );
                    }
                    else {
                        return (
                            <TextArea
                                key={key}
                                label={label}
                                required={required}
                                disabled={disabled}
                                value={value ? value : ''}
                                onChange={(e) => {
                                    setCurrentItem((prev) => ({
                                        ...prev,
                                        [key]: e.target.value,
                                    }));
                                }}
                            />
                        );
                    }
                case 'checkbox':
                    if (value === undefined || value === '') {
                        setCurrentItem((prev) => ({
                            ...prev,
                            [key]: false,
                        }));
                    }
                    return (
                        <Checkbox
                            key={key}
                            label={label}
                            required={required}
                            checked={value ? value : false}
                            onChange={(e) => {
                                setCurrentItem((prev) => ({
                                    ...prev,
                                    [key]: e.target.checked,
                                }));
                            }}
                        />
                    );
                case 'date':
                    return (
                        <DatePicker
                            key={key}
                            initialTimestamp={value ? value : ''}
                            disabled={disabled || role !== 'Admin'}
                            label={label}
                            required={required}
                            onChange={(unixTimeStamp) => {
                                setCurrentItem((prev) => ({
                                    ...prev,
                                    [key]: unixTimeStamp,
                                }));
                            }}
                        />
                    );
                case 'cell':
                    return (
                        <Cell
                            key={key}
                            value={value ? value : ''}
                            label={label}
                            required={required}
                            onChange={(data) => {
                                setCurrentItem((prev) => ({
                                    ...prev,
                                    [key]: data,
                                }));
                            }}
                        />
                    )
                case 'texteditor':
                    return (
                        <TextEditor
                            key={key}
                            value={value ? value : ''}
                            label={label}
                            required={required}
                            onChange={(data) => {
                                setCurrentItem((prev) => ({
                                    ...prev,
                                    [key]: data,
                                }));
                            }}
                        />
                    )
                case 'multiselect':
                    return (
                        <MultiSelectBox
                            key={key}
                            value={currentItem[key] || {}} // Ensure correct state handling
                            label={label}
                            list={Scopes}
                            required={required}
                            onChange={(data) => {
                                setCurrentItem((prev) => ({
                                    ...prev,
                                    [key]: data, // Update only the relevant key
                                }));
                            }}
                        />
                    )
                default:
                    return <h1>{type} needs a new component</h1>;
            }
        }
    };

    const renderInputFields = () => {
        if (page['ignoreKeys']) {
            return (
                <>
                    {Object.entries(currentItem)
                        .filter(([key]) => !page['ignoreKeys'].includes(key))
                        .map(([key, value]) => inputRenderHelper(key, value))}
                </>
            );
        }
    };

    const validateInputs = (item) => {
        const requiredKeys = page['requiredKeys'];
        const missingKeys = requiredKeys.filter(key => !item.hasOwnProperty(key) || item[key] === "" || item[key] === null || item[key] === undefined);
    
        if (missingKeys.length > 0) {
            let message = "Missing required fields:\n";
            missingKeys.forEach(key => {
                message += `- ${key}\n`;
            });
    
            window.alert(message);
            return false; // Stop submission
        }
    
        return true; // Validation successful
    };
    
    const actionFunction = async (actionType) => {
        setLoading(true);
    
        if (!validateInputs(currentItem)) {
            setLoading(false);
            return; // Stop execution if validation fails
        }
    
        try {
            if (actionType === "create") {
                await createItem(page.title, currentItem);
            }
            if (actionType === "update") {
                await updateItem(page.title, currentItem);
            }
            if (actionType === "delete") {
                await deleteItem(page.title, currentItem.id);
            }
            if (actionType === "create-contract-services") {
                await createContractServices(currentItem.id);
            }
            if (actionType === "receivePayment") {
                await receivePayment(currentItem.id);
            }
            if (actionType === "downloadInvoiceFile") {
                await downloadInvoiceFile(currentItem.invoiceNumber);
            }
            if (actionType === "downloadClientStatement") {
                await requestStatementCsv(currentItem.clientId);
            }
    
            navigate(-1); // Only navigate on successful action
        } catch (error) {
            console.error(`Error calling function ${actionType}: ${error.message}`);
        } finally {
            setLoading(false); // Always unset loading, even on error
        }
    };

    const renderButtons = () => {
        if (editMode) {
            if (page['editModeButtons']) {
                return (
                    <>
                        {page['editModeButtons'].map((button, index) => {
                            const [label, action] = Object.entries(button)[0];
    
                            return (
                                <Button
                                    key={index}
                                    onClick={() => actionFunction(action)}
                                    buttonText={label}
                                />
                            );
                        })}
                    </>
                );
            }
        }
        else {
            if (page['createModeButtons']) {
                return (
                    <>
                        {page['createModeButtons'].map((button, index) => {
                            const [label, action] = Object.entries(button)[0];
    
                            return (
                                <Button
                                    key={index}
                                    onClick={() => actionFunction(action)}
                                    buttonText={label}
                                />
                            );
                        })}
                    </>
                );
            }
        }
    };

    return (
        <>
            <Helmet>
                <meta name='robots' content='noindex' />
            </Helmet>
            <div className={styles.placeholder} />
            {loading ? (
                <Loader />
            ) : (
                <div className={styles.container}>
                    <h1>{page.name} Editor</h1>
                    <div className={styles.inputContainer}>{renderInputFields()}</div>
                    <div className={styles.buttonContainer}>{renderButtons()}</div>
                </div>
            )}
        </>
    );
};

export default Editor;
